import React, { useState, useEffect } from 'react';
import { Modal, Spin, Select, Form, Input, DatePicker, Button, Tooltip, Upload, message } from 'antd';
import { SaveFilled, EyeFilled, TagFilled } from '@ant-design/icons';
import { getExecutionTypes, getFrequencys } from "../../../../API/evaluation";
import { createComment } from "../../../../API/gaps";
import { handleOpenFile } from '../../../../API/utils';
import dayjs from 'dayjs';

const { TextArea } = Input;

const GapsDetailModal = ({
  visible,
  onCancel,
  selectedIssue,
  issueDetails,
  issueStatus,
  issueAreas,
  loading,
  onSave,
  api,
  gapDetails
}) => {
  const [form] = Form.useForm();
  const selectedStatusId = Form.useWatch('status', form);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [executionTypes, setExecutionTypes] = useState([]);
  const [frequencys, setFrequencys] = useState([]);
  const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.jpg', '.png'];
  const [uploadedFile, setUploadedFile] = useState(null);

  // Inicializar el formulario con los valores del issue y comentarios
  useEffect(() => {
    if (issueDetails) {
      form.setFieldsValue({
        date_committed: issueDetails.date_committed ? dayjs(issueDetails.date_committed) : null,
        date_finished: issueDetails.date_finished ? dayjs(issueDetails.date_finished) : null,
        area: issueDetails.responsible_area?.id || null,
        status: issueDetails.status?.id || null,  // ✅ Usar ID para el value del Select
        execution_type_id: issueDetails.execution_type?.id || null,
        frequency_id: issueDetails.frequency?.id || null
      });

      const issueComments = issueDetails.comments || [];
      const formattedComments = issueComments.map(comment => ({
        id: comment.id,
        text: comment.comment,
        date: comment.created_at || new Date().toISOString(),
        user: comment.user ? `${comment.user.first_name} ${comment.user.last_name}` : 'Sistema',
        username: comment.user?.username || 'unknown',
        created: comment.created
      }));
      
      setComments(formattedComments);
    } else {
      form.resetFields();
      setComments([]);
    }
  }, [issueDetails, form]);

  // Efecto para resetear el estado cuando cambia la issue
  useEffect(() => {
    if (visible && selectedIssue) {
      form.resetFields();
      setComments([]);
      setNewComment('');
      setIsSubmitting(false);
    }
  }, [selectedIssue, visible, form]);

  useEffect(() => {
    const finishedStatus = issueStatus?.find(s => s.index === 3);
    
    if (selectedStatusId === finishedStatus?.id) {
      // Auto-llenar fecha de finalización
      form.setFieldsValue({ 
        date_finished: dayjs()
      });
      
      // Si no tiene fecha comprometida, auto-llenarla también
      const currentDateCommitted = form.getFieldValue('date_committed');
      if (!currentDateCommitted) {
        form.setFieldsValue({
          date_committed: dayjs()
        });
      }
    } else {
      form.setFieldsValue({ date_finished: null });
      form.validateFields(['date_finished']).catch(() => {});
    }
  }, [selectedStatusId, form, issueStatus]);

  useEffect(() => {
    if (visible) {
      const fetchData = async () => {
        try {
          const [execRes, freqRes] = await Promise.all([
            getExecutionTypes(),
            getFrequencys()
          ]);

          setExecutionTypes(execRes || []);
          setFrequencys(freqRes || []);
        } catch (err) {
          console.error("Error cargando executionTypes/frequencys", err);
        }
      };
      fetchData();
    }
  }, [visible]);

  const handleAddComment = async () => {
    if (newComment.trim()) {
      try {
        setIsSubmitting(true);
        
        const commentData = {
          issue_code: selectedIssue.code,
          comment: newComment
        };
  
        const response = await createComment(commentData);
        
        const newCommentObj = {
          id: response.id,
          text: response.comment,
          date: new Date().toISOString(),
          user: `${response.user.first_name} ${response.user.last_name}`,
          username: response.user.username,
        };
  
        setComments(prev => [...prev, newCommentObj]);
        setNewComment('');
        api.success({ message: 'Comentario agregado correctamente' });
      } catch (error) {
        api.error({ message: 'Error al agregar el comentario' });
        console.error('Error adding comment:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const values = await form.validateFields();

      const data = {
        code: selectedIssue.code,
        type: issueDetails?.type || '',
        status_id: values.status,  // ✅ Envía el ID al backend
        responsible_area_id: values.area || 0,
        execution_type_id: values.execution_type_id,
        frequency_id: values.frequency_id,
        date_committed: values.date_committed ? values.date_committed.toISOString() : null,
        date_finished: values.date_finished ? values.date_finished.toISOString() : null,
      };

      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });

      if (uploadedFile) {
        formData.append("document_file", uploadedFile);
      }

      if (newComment.trim()) {
        const commentData = {
          issue_code: selectedIssue.code,
          comment: newComment.trim()
        };

        try {
          const commentResponse = await createComment(commentData);
          setComments(prev => [
            ...prev,
            {
              id: commentResponse.id,
              text: commentResponse.comment,
              date: new Date().toISOString(),
              user: `${commentResponse.user.first_name} ${commentResponse.user.last_name}`,
              username: commentResponse.user.username,
            },
          ]);
          setNewComment('');
        } catch (error) {
          console.error("Error creando comentario desde Guardar:", error);
        }
      }

      await onSave(formData);

      api.success({ message: 'Issue actualizada correctamente' });
      const finishedStatus = issueStatus?.find(s => s.index === 3);
      const previousStatusIndex = issueDetails?.status?.index;
      const newStatusId = values.status;
      
      if (previousStatusIndex !== 3 && newStatusId === finishedStatus?.id) {
        setTimeout(() => {
          window.location.reload();
        }, 600);
      }
      /* setTimeout(() => {
          window.location.reload();
        }, 600); */
      onCancel();
    } catch (error) {
      api.error({ message: "Error al guardar los cambios", key: 'save-gap-error' });
      console.error('Error saving changes:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const documentUrl = issueDetails?.document_file_url?.url || null;

  // ✅ Helper para obtener el index del estado actual
  const getCurrentStatusIndex = () => {
    const currentStatus = issueStatus?.find(s => s.id === selectedStatusId);
    return currentStatus?.index;
  };

  return (
    <Modal
      className="dependency-modal"
      key={selectedIssue?.code || 'modal'}
      title={
        gapDetails?.dependency_control?.control?.title ? (
          gapDetails.dependency_control.control.title.length > 55 ? (
            <Tooltip title={gapDetails.dependency_control.control.title}>
              {gapDetails.dependency_control.control.title.slice(0, 55) + "..."}
            </Tooltip>
          ) : (
            gapDetails.dependency_control.control.title
          )
        ) : (
          "N/A"
        )
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={isSubmitting}
      cancelButtonProps={{ className: 'cancel-button' }}
      okButtonProps={{
        className: `save-type-button ${issueDetails?.status?.index === 3 ? 'save-type-button-disabled' : ''}`,
        icon: <SaveFilled />,
        disabled: issueDetails?.status?.index === 3
      }}
      cancelText="Cancelar"
      okText={
        <Tooltip 
          title={issueDetails?.status?.index === 3 ? "No se puede editar una issue en estado Finalizado" : ""}
          placement="top"
        >
          <span>Guardar</span>
        </Tooltip>
      }
    >
      <Spin className="custom-spin" spinning={loading || isSubmitting}>
        <Form form={form} layout="vertical">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            
            <Form.Item label="Tipo">
              <Input value={issueDetails?.type || 'N/A'} disabled />
            </Form.Item>
            
            <Form.Item
              label="Estado"
              name="status"
              rules={[{ required: true, message: 'El estado es requerido' }]}
            >
              <Select placeholder="Seleccione un estado" disabled={issueDetails?.status?.index === 3}>
                {(issueStatus || []).map((status) => {
                  const shouldShowAllOptions = issueDetails?.flag;
                  
                  const hasPendingTasks = issueDetails?.tasks?.some(task => 
                    task.status && ![3, 4].includes(task.status.index)
                  );
                  
                  const isFinalizedStatus = status.index === 3;
                  let isDisabled = false;
                  let disabledReasons = [];
                  
                  if (!shouldShowAllOptions && isFinalizedStatus) {
                    isDisabled = true;
                    disabledReasons.push('Para finalizar esta issue, debe finalizar el tipo anterior.');
                  }
                  
                  if (isFinalizedStatus && hasPendingTasks) {
                    isDisabled = true;
                    disabledReasons.push('No puede finalizar la issue con tareas pendientes');
                  }
                  
                  const tooltipMessage = disabledReasons.join('\n');

                  if (isFinalizedStatus) {
                    return (
                      <Select.Option 
                        key={status.index} 
                        value={status.id}  // ✅ Value es el ID
                        disabled={isDisabled}
                        title={tooltipMessage}
                      >
                        <Tooltip title={isDisabled ? tooltipMessage : ''}>
                          <span style={{ opacity: isDisabled ? 0.6 : 1 }}>
                            {status.name}
                            {isDisabled && ' ⚠️'}
                          </span>
                        </Tooltip>
                      </Select.Option>
                    );
                  }
                  
                  return (
                    <Select.Option key={status.index} value={status.id}>
                      {status.name}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>

            <Form.Item 
              label="Área"
              name="area"
            >
              <Select placeholder="Seleccione un área" disabled>
                {(issueAreas || []).map((area) => (
                  <Select.Option key={area.id} value={area.id}>
                    {area.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
              
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item 
              label="Fecha Comprometida" 
              name="date_committed"
              required={(() => {
                const initiatedStatus = issueStatus?.find(s => s.index === 2);
                return selectedStatusId === initiatedStatus?.id;
              })()}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const statusId = getFieldValue('status');
                    const initiatedStatus = issueStatus?.find(s => s.index === 2);
                    const pendingStatus = issueStatus?.find(s => s.index === 1);
                    
                    // Estado Pendiente => NO obligatorio
                    if (statusId === pendingStatus?.id) {
                      return Promise.resolve();
                    }
                    
                    // Estado Iniciado => Obligatorio
                    if (statusId === initiatedStatus?.id && !value) {
                      return Promise.reject(new Error('La fecha comprometida es obligatoria'));
                    }
                    
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD/MM/YYYY"
                inputReadOnly
                disabled={(() => {
                  const finishedStatus = issueStatus?.find(s => s.index === 3);
                  const pendingStatus = issueStatus?.find(s => s.index === 1);
                  const currentDateCommitted = form.getFieldValue('date_committed');
                  const previousStatusId = issueDetails?.status?.id;
                  
                  return (
                    issueDetails?.status?.index === 3 ||
                    (selectedStatusId === finishedStatus?.id && currentDateCommitted) ||
                    (previousStatusId === pendingStatus?.id && selectedStatusId === finishedStatus?.id)
                  );
                })()}
                disabledDate={(current) => {
                  return current && current < dayjs().startOf('day');
                }}
              />
            </Form.Item>
              
            <Form.Item 
              label="Fecha Finalización" 
              name="date_finished"
              inputReadOnly
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const statusId = getFieldValue('status');
                    const finishedStatus = issueStatus?.find(s => s.index === 3);
                    
                    // ✅ Comparar con el ID del estado "Finalizado"
                    if (statusId === finishedStatus?.id && !value) {
                      return Promise.reject(new Error('Este campo es requerido'));
                    }
                    
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD/MM/YYYY"
                disabled={true}
                /* disabled={(() => {
                  const finishedStatus = issueStatus?.find(s => s.index === 3);
                  // Bloquear edición si: El estado actual seleccionado es Finalizado O si la issue ya está finalizada (por backend)
                  return (
                    selectedStatusId === finishedStatus?.id || 
                    issueDetails?.status?.index === 3
                  );
                })()} */
                disabledDate={(current) => {
                  return current && current < dayjs().startOf('day');
                }}
              />
            </Form.Item>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item 
              label="Tipo de Ejecución"
              name="execution_type_id"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const statusId = getFieldValue('status');
                    const finishedStatus = issueStatus?.find(s => s.index === 3);
                    const pendingStatus = issueStatus?.find(s => s.index === 1);
                    
                    // ✅ Requerido si es tipo "Diseño" y estado es "Finalizado"
                    if (
                      issueDetails?.type === "Diseño" 
                      && statusId === finishedStatus?.id 
                      && issueDetails?.status?.index !== 3
                      && !value
                    ) {
                      return Promise.reject(new Error('Este campo es requerido'));
                    }
                    
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Select 
                placeholder="Tipo de Ejecución"
                disabled={(() => {
                  const pendingStatus = issueStatus?.find(s => s.index === 1);
                  return (
                    issueDetails?.type !== "Diseño" 
                    || selectedStatusId === pendingStatus?.id
                    || issueDetails?.status?.index === 3
                  );
                })()}
              >
                {executionTypes.map((exec) => (
                  <Select.Option key={exec.id} value={exec.id}>
                    {exec.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item 
              label="Frecuencia"
              name="frequency_id"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const statusId = getFieldValue('status');
                    const finishedStatus = issueStatus?.find(s => s.index === 3);
                    
                    if (
                      issueDetails?.type === "Diseño" 
                      && statusId === finishedStatus?.id 
                      && issueDetails?.status?.index !== 3
                      && !value
                    ) {
                      return Promise.reject(new Error('Este campo es requerido'));
                    }
                    
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Select 
                placeholder="Frecuencia"
                disabled={(() => {
                  const pendingStatus = issueStatus?.find(s => s.index === 1);
                  return (
                    issueDetails?.type !== "Diseño" 
                    || selectedStatusId === pendingStatus?.id
                    || issueDetails?.status?.index === 3
                  );
                })()}
              >
                {frequencys.map((freq) => (
                  <Select.Option key={freq.id} value={freq.id}>
                    {freq.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
            <Form.Item
              label="Documentación del Control"
              name="document_file"
              style={{ display: issueDetails?.type === "Documentación" ? 'block' : 'none' }}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const statusId = getFieldValue('status');
                    const finishedStatus = issueStatus?.find(s => s.index === 3);
                    
                    if (issueDetails?.type === "Documentación" && statusId === finishedStatus?.id && !uploadedFile && !documentUrl) {
                      return Promise.reject(new Error('El documento es obligatorio'));
                    }
                    
                    if (value && value.file) {
                      const ext = value.file.name.substring(value.file.name.lastIndexOf('.'));
                      if (!allowedExtensions.includes(ext.toLowerCase())) {
                        return Promise.reject(new Error('Formato de archivo no permitido'));
                      }
                    }
                    
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Upload
                  beforeUpload={(file) => {
                    const ext = file.name.substring(file.name.lastIndexOf('.'));
                    if (!allowedExtensions.includes(ext.toLowerCase())) {
                      message.error('Formato de archivo no permitido');
                      return Upload.LIST_IGNORE;
                    }

                    setUploadedFile(file);

                    api.success({
                      message: "Documento agregado",
                      description: `Se cargó correctamente "${file.name}"`,
                    });

                    return false;
                  }}
                  onRemove={() => {
                    setUploadedFile(null);
                    api.info({
                      message: "Documento eliminado",
                      description: "Se quitó el archivo cargado",
                    });
                  }}
                  showUploadList={false}
                >
                  <Button 
                    className='document-button' 
                    icon={<TagFilled />}
                    disabled={issueDetails?.status?.index === 3}
                  >
                    Subir Documento
                  </Button>
                </Upload>

                {(uploadedFile || documentUrl) && (
                  <Tooltip title="Ver Documento">
                    <Button
                      type="link"
                      icon={<EyeFilled />}
                      onClick={() => {
                        if (uploadedFile) {
                          const blobUrl = URL.createObjectURL(uploadedFile);
                          window.open(blobUrl, "_blank");
                        } else if (documentUrl) {
                          handleOpenFile(documentUrl, 'file');
                        }
                      }}
                    />
                  </Tooltip>
                )}
              </div>
            </Form.Item>
          </div>

          <Form.Item label="Comentarios" style={{ marginBottom: 24 }}>
            <div className="comments-container">
              <div className={`comments-input-area ${comments.length > 0 ? 'with-border' : ''}`}>
                <TextArea
                  className="comments-textarea"
                  disabled={issueDetails?.status?.index === 3}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Escribe tu comentario (máx. 300 caracteres)"
                  autoSize={{ minRows: 3, maxRows: 3 }}
                  maxLength={300}
                />
                <div className="comments-actions">
                  <span className="comments-counter">
                    {newComment.length}/300
                  </span>
                  <Button 
                    type="primary"
                    size="small"
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || isSubmitting}
                    loading={isSubmitting}
                    className="comments-button"
                  >
                    Comentar
                  </Button>
                </div>
              </div>
              
              {comments.length > 0 ? (
                <div className="comments-list">
                  {comments.map((comment) => (
                    <div key={comment.id} className="comment-item">
                      <div className="comment-header">
                        <span className="comment-user">{comment.user}</span>
                        <span className="comment-date">
                          {dayjs(comment.created).format('DD/MM/YYYY HH:mm')}
                        </span>
                      </div>
                      <div className="comment-text">
                        {comment.text}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="comments-empty">
                  No hay comentarios todavía
                </div>
              )}
            </div>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default GapsDetailModal;